import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, Send, Check } from 'lucide-react';
import { contactInfo } from '../../data';

const Contact = () => {
  const [status, setStatus] = useState('idle'); // idle, loading, success, error

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="pt-32 pb-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <div>
            <h2 className="text-3xl md:text-4xl font-medium text-slate-900 mb-6 uppercase">
              Klar til dit næste golfophold?
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-10">
              Ring, skriv eller send en besked – så hjælper vi dig med at finde et ophold, der passer til dine ønsker. Vi glæder os til at høre fra dig.
            </p>

            <div className="space-y-8">
              <a 
                href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
                className="flex items-center gap-6 p-6 bg-white rounded-2xl shadow-sm border border-slate-100 hover:border-[#39d14b]/30 transition-all group"
              >
                <div className="text-slate-900">
                  <Phone size={28} />
                </div>
                <div>
                  <p className="text-sm text-slate-500 font-medium uppercase tracking-wider">Ring til os</p>
                  <p className="text-xl font-medium text-slate-900">{contactInfo.phone}</p>
                </div>
              </a>

              <a 
                href={`mailto:${contactInfo.email}`}
                className="flex items-center gap-6 p-6 bg-white rounded-2xl shadow-sm border border-slate-100 hover:border-[#39d14b]/30 transition-all group"
              >
                <div className="text-slate-900">
                  <Mail size={28} />
                </div>
                <div>
                  <p className="text-sm text-slate-500 font-medium uppercase tracking-wider">Send en email</p>
                  <p className="text-xl font-medium text-slate-900">{contactInfo.email}</p>
                </div>
              </a>
            </div>

            <div className="mt-12 p-8 bg-[var(--brand)] rounded-3xl text-white relative overflow-hidden">
              <div className="relative z-10">
                <h3 className="text-xl font-medium mb-4 uppercase">Hvorfor kontakte os?</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-white">
                    <Check size={20} className="text-white shrink-0 pt-1" />
                    <span>Personlig rådgivning fra start til slut</span>
                  </li>
                  <li className="flex items-start gap-3 text-white">
                    <Check size={20} className="text-white shrink-0 pt-1" />
                    <span>Skræddersyede tilbud uden forpligtelse</span>
                  </li>
                  <li className="flex items-start gap-3 text-white">
                    <Check size={20} className="text-white shrink-0 pt-1" />
                    <span>Eksperthjælp til baner og bolig</span>
                  </li>
                </ul>
              </div>
              <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white/5 rounded-full blur-2xl"></div>
            </div>
          </div>

          {/* Form */}
          <div className="bg-white p-8 md:p-10 rounded-[2.5rem] shadow-xl border border-slate-100">
            {status === 'success' ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <div className="w-20 h-20 bg-[#39d14b]/10 text-[#39d14b] rounded-full flex items-center justify-center mb-6">
                  <CheckCircle2 size={40} />
                </div>
                <h3 className="text-2xl font-medium text-slate-900 mb-4 uppercase">Tak for din besked!</h3>
                <p className="text-slate-600 mb-8">
                  Vi har modtaget din henvendelse og vender tilbage til dig hurtigst muligt.
                </p>
                <button 
                  onClick={() => setStatus('idle')}
                  className="px-8 py-3 bg-[#39d14b] text-white font-medium rounded-full hover:-translate-y-1 transition-all"
                >
                  Send en ny besked
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot field */}
                <div className="sr-only absolute opacity-0 pointer-events-none" aria-hidden="true">
                  <label htmlFor="website_url">Website URL</label>
                  <input type="text" name="website_url" id="website_url" tabIndex="-1" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label htmlFor="name" className="text-sm font-medium text-slate-700 ml-1 uppercase tracking-wider">Navn</label>
                    <input 
                      type="text" 
                      id="name" 
                      name="name" 
                      required 
                      placeholder="Dit navn"
                      className="w-full px-5 py-4 bg-white border border-slate-200 rounded-2xl focus:ring-2 focus:ring-[#39d14b] focus:border-[#39d14b] outline-none transition-all"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium text-slate-700 ml-1 uppercase tracking-wider">Email</label>
                    <input 
                      type="email" 
                      id="email" 
                      name="email" 
                      required 
                      placeholder="din@email.dk"
                      className="w-full px-5 py-4 bg-white border border-slate-200 rounded-2xl focus:ring-2 focus:ring-[#39d14b] focus:border-[#39d14b] outline-none transition-all"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="phone" className="text-sm font-medium text-slate-700 ml-1 uppercase tracking-wider">Telefon (valgfrit)</label>
                  <input 
                    type="tel" 
                    id="phone" 
                    name="phone" 
                    placeholder="Dit telefonnummer"
                    className="w-full px-5 py-4 bg-white border border-slate-200 rounded-2xl focus:ring-2 focus:ring-[#39d14b] focus:border-[#39d14b] outline-none transition-all"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="message" className="text-sm font-medium text-slate-700 ml-1 uppercase tracking-wider">Besked</label>
                  <textarea 
                    id="message" 
                    name="message" 
                    rows="5" 
                    required 
                    placeholder="Hvordan kan vi hjælpe dig?"
                    className="w-full px-5 py-4 bg-white border border-slate-200 rounded-2xl focus:ring-2 focus:ring-[#39d14b] focus:border-[#39d14b] outline-none transition-all resize-none"
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  disabled={status === 'loading'}
                  className="w-full py-5 bg-[#39d14b] text-white font-medium text-lg rounded-full hover:-translate-y-1 transition-all shadow-lg shadow-[#39d14b]/20 flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed group"
                >
                  {status === 'loading' ? (
                    <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <span className="uppercase tracking-wider">Send besked</span>
                      <Send size={20} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                    </>
                  )}
                </button>
                
                {status === 'error' && (
                  <p className="text-red-500 text-sm text-center font-medium">
                    Der skete en fejl. Prøv venligst igen eller ring til os.
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
